/*
 * <bb-product-lightbox> — wires PhotoSwipe v5 to a parent <media-gallery>.
 *
 * Rendered inside <media-gallery> by snippets/product-media-gallery.liquid
 * when `section.settings.image_zoom == 'lightbox'`. The closest <media-gallery>
 * is the PhotoSwipe gallery root; image-type thumbnails inside it are wrapped
 * in <a href data-pswp-width data-pswp-height> by snippets/product-thumbnail.liquid.
 *
 * Lifecycle: connectedCallback initializes a PhotoSwipeLightbox; disconnectedCallback
 * destroys it. Shopify section-rendering events (shopify:section:load, :unload,
 * :reorder) all fire connect/disconnect on this element implicitly — no manual
 * event listeners needed. Same pattern as the rest of the bb-*.js custom elements
 * in this theme.
 *
 * Quick-add safety: assets/quick-add.js preprocessHTML strips this element from
 * fetched section HTML, so it never connects inside <quick-add-modal>. The
 * closest('quick-add-modal') check below is belt-and-braces.
 *
 * Hunter Bell NYC's wrapper (lightbox.js on hunterbellnyc.com) was the reference;
 * deviations are documented inline.
 */

const BPL_ARROW_SVG =
  '<svg aria-hidden="true" focusable="false" class="pswp__icn" viewBox="0 0 18 18"><path d="M11.5 3 5 9l6.5 6" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.6" fill="none"/></svg>';
const BPL_CLOSE_SVG =
  '<svg aria-hidden="true" focusable="false" class="pswp__icn" viewBox="0 0 18 18"><path d="M3 3l12 12M3 15 15 3" stroke="currentColor" stroke-linecap="round" stroke-width="1.4" fill="none"/></svg>';

if (!customElements.get('bb-product-lightbox')) {
  customElements.define(
    'bb-product-lightbox',
    class BbProductLightbox extends HTMLElement {
      connectedCallback() {
        if (typeof PhotoSwipe === 'undefined' || typeof PhotoSwipeLightbox === 'undefined') {
          console.warn('[bb-product-lightbox] PhotoSwipe global missing; aborting init.');
          return;
        }

        const gallery = this.closest('media-gallery');
        if (!gallery) return;
        if (gallery.closest('quick-add-modal')) return;

        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const mobilePortrait = window.matchMedia('(max-width: 749px) and (orientation: portrait)').matches;

        try {
          this.lightbox = new PhotoSwipeLightbox({
            gallery,
            children: 'a[data-pswp-width]',
            pswpModule: PhotoSwipe,

            // Hunter Bell parity
            bgOpacity: 1,
            loop: true,
            counter: true,
            zoom: false,
            bgClickAction: false,

            // Our deviations
            allowPanToNext: false, // when zoomed, pan stays on current image
            closeOnVerticalDrag: !mobilePortrait, // avoid collision with pinch on tall portraits
            showHideAnimationType: reducedMotion ? 'none' : 'zoom',
            zoomAnimationDuration: reducedMotion ? 0 : 333,
            secondaryZoomLevel: (z) => z.fit * 3,
            maxZoomLevel: (z) => z.fit * 4,

            arrowPrevSVG: BPL_ARROW_SVG,
            arrowNextSVG: BPL_ARROW_SVG, // rotated 180° via CSS on .pswp__button--arrow--next
            closeSVG: BPL_CLOSE_SVG,
          });
          this.lightbox.init();
        } catch (e) {
          console.error('[bb-product-lightbox] init failed:', e);
          this.lightbox = null;
        }
      }

      disconnectedCallback() {
        if (this.lightbox) {
          this.lightbox.destroy();
          this.lightbox = null;
        }
      }
    }
  );
}
